import React, { useState } from 'react';
import Select from 'epn-ui/umd/dist/components/Select';
import omit from 'lodash/omit';

import { faTrash } from '@fortawesome/pro-regular-svg-icons/faTrash';
import { faQuestionCircle } from '@fortawesome/pro-regular-svg-icons/faQuestionCircle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TooltipPro from 'epn-ui/umd/dist/components/TooltipPro';
import Grid from 'epn-ui/umd/dist/components/Grid';
import BaseEmpty from '@components/base/BaseEmpty';
import useI18n from '@i18n';

import { IGetBotUsersDataFormatted } from '@api/types';
import styles from './styles.module.scss';

const ROLES = ['head', 'user'];

const TRANSLATE_ROLE_KEY = {
    head: 'webmaster',
    user: 'assistant',
};

interface ITelegramBotTable {
    users: IGetBotUsersDataFormatted[];
    deleteUser: (user: IGetBotUsersDataFormatted) => void;
    updateUser: (user: IGetBotUsersDataFormatted) => void;
    actionTooltipId: number | string;
}

const Table: React.FC<ITelegramBotTable> = ({
    users = [],
    deleteUser,
    updateUser,
    actionTooltipId,
}) => {
    const { t } = useI18n('TelegramBot');
    const [edited, setEdited] = useState({});

    // const onStartEdit = (id: string | number) => (): void =>
    //     setEdited({
    //         ...edited,
    //         [id]: true,
    //     });

    const onEndEdit = (newValue, user: IGetBotUsersDataFormatted): void => {
        setEdited(omit(edited, user.id));
        updateUser({ ...user, role: newValue });
    };

    const options = ROLES.map(el => ({
        value: el,
        label: t(TRANSLATE_ROLE_KEY[el]),
    }));
    const getValue = (role: string) => options.find(el => el.value === role);

    const textClass =
        'block text-[14px] leading-p2  text-blueGray-500 overflow-hidden overflow-ellipsis whitespace-nowrap';
    const textHeaderClass =
        'relative block overflow-hidden text-blueGray-600 text-[14px] leading-[21px] overflow-ellipsis whitespace-nowrap xs:text-[16px] xs:leading-p1';
    const boxClass = 'block p-[8px] xs:p-[16px] text-blueGray-500';

    return (
        <div className="flex flex-col">
            <div>
                <Grid gutter={[0, 0]}>
                    <Grid.Col span={7} className={boxClass}>
                        <p className={textHeaderClass}>{t('Login')}</p>
                    </Grid.Col>
                    <Grid.Col span={7} className={boxClass}>
                        <p className={textHeaderClass}>{t('Username')}</p>
                    </Grid.Col>
                    <Grid.Col span={7} className={boxClass}>
                        <p className={textHeaderClass}>{t('Role')}</p>
                    </Grid.Col>
                    <Grid.Col span={3} className={boxClass}>
                        <TooltipPro
                            title={<p className="max-w-[65vw]">{t(`${actionTooltipId}`)}</p>}
                            placement="left"
                        >
                            <p className="text-center text-blueGray-600 text-[14px] leading-[21px] xs:text-[16px] xs:leading-p1">
                                <FontAwesomeIcon
                                    className="ml-[8px] hover:cursor-pointer"
                                    icon={faQuestionCircle}
                                />
                            </p>
                        </TooltipPro>
                    </Grid.Col>
                </Grid>
            </div>
            <div>
                {users.map(user => (
                    <div key={user.id} className={styles.rowItem}>
                        <Grid gutter={[0, 0]} align="middle" className="items-center">
                            <Grid.Col span={7} className={boxClass}>
                                <p className={textClass}>@{user.messengerUserName}</p>
                            </Grid.Col>
                            <Grid.Col span={7} className={boxClass}>
                                <p className={textClass}>{user.messengerUserFirstName}</p>
                            </Grid.Col>
                            <Grid.Col span={7} className={boxClass}>
                                <Select
                                    value={getValue(user.role)}
                                    onSelect={value => onEndEdit(value, user)}
                                    options={options}
                                    className="w-[80px] xs:w-full"
                                    notFoundContent={() => <BaseEmpty />}
                                />
                            </Grid.Col>
                            <Grid.Col span={3} className={boxClass}>
                                <p className={`${textClass} text-[16px] text-center`}>
                                    <FontAwesomeIcon
                                        className="ml-[10px] hover:cursor-pointer hover:text-brandColor"
                                        icon={faTrash}
                                        onClick={() => deleteUser(user)}
                                    />
                                </p>
                            </Grid.Col>
                        </Grid>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Table;
